/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, useId, useMemo } from 'react';
import type { ShopperSearch } from '@/scapi';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { Label } from '@/components/forms/label';
import type { RefinementProps } from '../types';

export default function RefinePrice({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps & {
    result?: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement {
    const radioGroupId = useId();

    // Pick the currently-selected predefined range (price is an exclusive refinement —
    // only one of the buckets can be applied at a time, per `category-refinements/index.tsx`).
    const selectedRangeValue = useMemo(
        () => values.find((value) => isFilterSelected(attributeId, value.value))?.value ?? '',
        [values, attributeId, isFilterSelected]
    );

    return (
        <div className="mt-2 flex flex-col gap-4">
            {/*
             * Predefined price ranges (Figma node 13055:342244 "PRICE" section).
             * Radios — price is exclusive: selecting a new bucket replaces the previous
             * one via `toggleFilter`'s exclusive-refinement branch.
             */}
            {values.length > 0 && (
                <RadioGroup
                    value={selectedRangeValue}
                    onValueChange={(value) => toggleFilter(attributeId, value)}
                    aria-label="Price range"
                    className="flex flex-col gap-1">
                    {values.map((value, idx) => {
                        const id = `${radioGroupId}-${idx}`;
                        return (
                            <Label
                                key={`${attributeId}:${value.value}`}
                                htmlFor={id}
                                className="flex w-full cursor-pointer items-center gap-2 py-1">
                                <RadioGroupItem id={id} value={value.value} />
                                <span className="text-sm font-normal leading-5 text-foreground">
                                    {value.label || value.value}
                                    {value.hitCount !== undefined && (
                                        <span className="ml-1 tabular-nums text-muted-foreground">
                                            ({value.hitCount})
                                        </span>
                                    )}
                                </span>
                            </Label>
                        );
                    })}
                </RadioGroup>
            )}
        </div>
    );
}
